import { useState } from "react";
import { useAuth } from "./contexts/AuthContext";
import { BottomNav, type MainTab } from "./components/BottomNav";
import { RankingScreen } from "./screens/RankingScreen";
import { Header } from "./components/Header";
import { MonthSelector } from "./components/MonthSelector";
import { ViewSelector } from "./components/ViewSelector";
import { AdminScreen } from "./screens/AdminScreen";
import { EmployeeScreen } from "./screens/EmployeeScreen";
import { StoresScreen } from "./screens/StoresScreen";
import { parseView } from "./lib/retail";
import { getTg } from "./lib/tg";

const BOT_LINK = import.meta.env.VITE_BOT_LINK || "https://t.me/";

function InfoCard({ icon, title, note }: { icon: string; title: string; note: string }) {
  return (
    <div className="p-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
        <div className="text-4xl mb-2">{icon}</div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-600 mt-1.5 leading-relaxed">{note}</div>
      </div>
    </div>
  );
}

/** Главный экран: сотрудница — своя карточка; владелец — тот же экран плюс режимы «все магазины / магазин». */
function MainContent({ tab }: { tab: MainTab }) {
  const { me, t, availableMonths, monthStatuses, month, view, setView, retail, retailLoading, retailError } = useAuth();
  if (!me) return null;
  if (availableMonths.length === 0) return <InfoCard icon="📭" title={t("no_months_title")} note={t("no_months_note")} />;

  const final = monthStatuses.find(s => s.month === month)?.status === "final";
  const body = (() => {
    if (tab === "rating") return <RankingScreen />;
    if (retailLoading && !retail) return <div className="p-6 text-slate-500 text-sm">{t("err_loading")}</div>;
    if (retailError === "NO_DATA" || (!retail && !retailLoading)) return <InfoCard icon="📭" title={t("no_data_title")} note={t("no_data_note")} />;
    if (retailError) return <div className="p-6 text-rose-600 text-sm">{retailError}</div>;
    if (!retail) return null;

    if (!me.is_admin) {
      // Администратор магазина: карточка своего магазина + список сотрудниц с нетто и итог (store_rows с сервера), без карточек отдельных сотрудниц.
      const ev = parseView(view);
      if (ev.kind === "store" && me.view_store === ev.store) {
        return <StoresScreen retail={retail} store={ev.store} final={final} storeAdmin onSelectPerson={() => {}} />;
      }
      const row = retail.rows[0];
      if (!row) return <InfoCard icon="📭" title={t("no_data_title")} note={t("no_data_note")} />;
      return <EmployeeScreen row={row} isAdmin={false} month={month} final={final} />;
    }
    const v = parseView(view);
    if (v.kind === "person") {
      const row = retail.rows.find(r => r.person_id === v.personId);
      if (!row) return <InfoCard icon="📭" title={t("no_data_title")} note={t("no_data_note")} />;
      return <EmployeeScreen row={row} isAdmin month={month} final={final} />;
    }
    return <StoresScreen retail={retail} store={v.kind === "store" ? v.store : null} final={final} onSelectPerson={id => setView(`p:${id}`)} />;
  })();

  return (
    <>
      <ViewSelector />
      <MonthSelector />
      {body}
    </>
  );
}

export default function App() {
  const { me, error, maintenanceMsg, t, lang, showAdmin } = useAuth();
  const [tab, setTab] = useState<MainTab>("kpi");

  const tg = getTg();
  const isInTelegram = !!tg && !!tg.initData;

  if (!isInTelegram) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-100">
        <div className="bg-white rounded-2xl p-6 text-center border border-slate-200 shadow max-w-sm w-full">
          <div className="text-5xl mb-3">📱</div>
          <div className="text-base font-semibold text-slate-900">{t("not_in_telegram_title")}</div>
          <div className="text-sm text-slate-600 mt-2 leading-relaxed">{t("not_in_telegram_note")}</div>
          <a href={BOT_LINK} className="mt-4 inline-block bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl px-5 py-2.5 transition">{t("not_in_telegram_btn")}</a>
        </div>
      </div>
    );
  }

  if (error === "MAINTENANCE") {
    const msg = lang === "uk" ? maintenanceMsg.uk : maintenanceMsg.ru;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-100">
        <div className="bg-white rounded-2xl p-6 text-center border border-slate-200 shadow max-w-sm w-full">
          <div className="text-5xl mb-3">🔧</div>
          <div className="text-base font-semibold text-slate-900">{t("maintenance_title")}</div>
          <div className="text-sm text-slate-600 mt-2 leading-relaxed whitespace-pre-line">{msg || t("maintenance_fallback")}</div>
        </div>
      </div>
    );
  }

  if (error === "ACCESS_REVOKED" || error === "USER_NOT_REGISTERED") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-100">
        <div className="bg-white rounded-2xl p-6 text-center border border-slate-200 shadow">
          <div className="text-4xl mb-2">🔒</div>
          <div className="text-base font-semibold text-slate-900">{t("err_access_denied")}</div>
          <div className="text-sm text-slate-600 mt-1">{t("err_access_admin_note")}</div>
        </div>
      </div>
    );
  }
  if (error) return <div className="p-6 text-rose-600">Ошибка: {error}</div>;
  if (!me) return <div className="p-6 text-slate-500">{t("err_loading")}</div>;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <Header />
      <main className="flex-1 overflow-y-auto pb-20">
        <MainContent tab={tab} />
      </main>
      <BottomNav tab={tab} onChange={setTab} />
      {showAdmin && <AdminScreen />}
    </div>
  );
}
